<template>
    <modal :title="title" :id="id" :saveAction="false" :actionButtons="false" size="full" @shown="shown" @hidden="hidden">
        <div id="printContent" v-if="data.teeth.length > 0">
            <div class="container page-break-after">
                <div class="row">
                    <div class="col-12">
                        <company-print-header></company-print-header>
                    </div>
                    <div class="col-12"><hr></div>
                    <div class="col-12">
                        <div class="row">
                            <div class="mb-3 mb-md-2 col-12">
                                <span class="text-muted h4">{{$t('Work order')}} #{{lab.labNumber}} / {{ lab.createdAt | moment('DD MMMM, YYYY') }}</span>
                            </div>
                            <div class="mb-3 mb-sm-auto col-6 col-sm-auto">
                                <div class="text-muted" v-if="user">
                                    <strong class="text-body">{{ $t('Doctor') }}</strong>
                                    <div>{{ user.firstName }} {{ user.lastName }}</div>
                                </div>
                            </div>
                            <div class="mb-3 mb-sm-auto col-6 col-sm-auto">
                                <div class="text-muted" v-if="technician">
                                    <strong class="text-body">{{ $t('Technician') }}</strong>
                                    <div>{{ technician.name }}</div>
                                </div>
                            </div>
                            <div class="mb-3 mb-sm-auto col-6 col-sm-auto">
                                <div class="text-muted" v-if="data.dueDate">
                                    <strong class="text-body">{{ $t('Completion date') }}</strong>
                                    <div>{{ dateFormatHandler(data.dueDate) }}</div>
                                </div>
                            </div>
                            <div class="mb-3 mb-sm-auto col-6 col-sm-auto">
                                <div class="text-muted">
                                    <strong class="d-block text-body">{{ $t('Fittings') }}</strong>
                                    <template v-if="data.fittings && data.fittings.length > 0">
                                        <span class="mr-3" v-for="(fitting, fittingIndex) in data.fittings" :key="fittingIndex">
                                            {{ dateFormatHandler(fitting.date) }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <span class="mr-3 empty-area"
                                            style="width: 122px;"
                                            v-for="(item, index) in 2" :key="index">&nbsp;</span>
                                    </template>
                                </div>
                            </div>
                            <div class="col d-none d-sm-block"></div>
                            <div class="mb-3 mb-sm-auto col-6 col-sm-auto">
                                <div class="text-muted mt-0" v-if="customer">
                                    <strong class="text-body">{{ $t('Customer') }}</strong>
                                    <div>{{ customer.first_name }} {{ customer.last_name }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 mb-4">
                        <teeth-map :value="teethNumbers" :tooths="[]" :width="20"></teeth-map>
                    </div>
                    <div class="col-12">
                        <div class="row">
                            <div :class="group.teeth.length <= 6 ? 'col-12 col-sm-6':'col-12'" v-for="(group, index) in data.teeth" :key="index">
                                <div class="mb-4">
                                    <p class="mb-3">{{$t('Kind of work')}}</p>
                                    <h4 class="text-muted mt-2" v-if="group.treatment && Object.keys(group.treatment).length">
                                        <strong>{{group.treatment.code}} - </strong>
                                        <span style="line-height: 22px; font-size: 14px;">{{group.treatment.label}}</span>
                                    </h4>
                                    <h4 class="text-muted mt-2" v-else>
                                        <span class="empty-area" style="width: 180px;">&nbsp;</span>
                                    </h4>
                                </div>

                                <div v-if="group.teethColors && Object.keys(group.teethColors).length" class="text-center mb-3">
                                    <template v-for="(tooth, index) in group.teeth">
                                        <div v-if="group.teethColors[tooth]" style="display: inline-block" :key="index">
                                            <span v-if="Object.keys(group.teethColors).length > 1" class="badge badge-pill badge-light m-2">
                                                {{ tooth }}
                                            </span>
                                            <div class="card card--paint mb-2">
                                                <div class="card-body p-3">
                                                    <paint :value="group.teethColors[tooth]"
                                                        width="150px"
                                                        height="150px"
                                                        readonly
                                                        class="mt-2 mb-2">
                                                    </paint>
                                                    <div class="text-muted d-none" style="min-height: 22px;">
                                                        <template v-for="item in group.teethColors[tooth]">
                                                            {{item}}
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <div v-else class="text-center mb-3">
                                    <div class="d-inline-block card card--paint">
                                        <div class="card-body p-3">
                                            <paint
                                                width="100px"
                                                height="100px"
                                                readonly>
                                            </paint>
                                            <div class="empty-area w-100" style="min-height: 22px;"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div :class="data.teeth[0].teeth.length <= 6 ? 'col-12 col-sm-6':'col-12'">
                                <div class="row">
                                    <div class="mb-4" :class="data.teeth[0].teeth.length < 6 ? 'col-12 col-sm-4':'col-6'">
                                        <div class="blockquote mb-3">
                                            <p class="mb-2">{{$t('For sending')}}</p>
                                            <ul
                                                v-if="data.forSending && data.forSending.length > 0"
                                                style="list-style:circle;color:#95AAC9;padding-left:20px;"
                                            >
                                                <li v-for="(sendingItem, index) in data.forSending" :key="index">
                                                    <span
                                                        class="text-muted"
                                                        style="font-size: 0.9375rem"
                                                    >
                                                        {{sendingItem.text}} {{sendingItem.qty}}
                                                    </span>
                                                </li>
                                            </ul>
                                            <ul
                                                v-else
                                                style="list-style:circle;color:#95AAC9;padding-left:20px;"
                                            >
                                                <li
                                                    v-for="(item, index) in 4" :key="index"
                                                    :class="data.teeth[0].teeth.length > 6 ? 'd-inline-block mr-3':''"
                                                >
                                                    <span class="empty-area" style="width: 120px;">&nbsp;</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div class="mb-4" :class="data.teeth[0].teeth.length < 6 ? 'col-12 col-sm-8':'col-6'">
                                        <div class="blockquote mb-3">
                                            <p class="mb-2">{{$t('Face shape')}}</p>
                                            <em
                                                v-if="data.faceShape"
                                                class="text-muted text-justify"
                                                style="font-size: 0.9375rem"
                                            >
                                                {{$t(capitalize(data.faceShape))}}
                                            </em>
                                            <span v-else class="empty-area" style="width: 120px;">&nbsp;</span>
                                        </div>
                                        <div class="blockquote mb-3">
                                            <p class="mb-2">{{$t('Wishes')}}</p>
                                            <em
                                                v-if="data.note"
                                                class="text-muted text-justify"
                                                style="font-size: 0.9375rem"
                                            >
                                                {{data.note}}
                                            </em>
                                            <template v-else v-for="(item, index) in 3">
                                                <span
                                                    class="empty-area w-100"
                                                    :class="data.teeth[0].teeth.length > 6 ? 'd-inline-block mr-3':''"
                                                    :key="index"
                                                >
                                                    &nbsp;
                                                </span>
                                            </template>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <template v-if="data.teeth[0].teeth.length <= 14 || (data.teeth[0].teeth.length > 14 && !isTableDisplay)">
                    <div class="row" v-for="(group, index) in data.teeth" :key="index">
                        <div :class="isTableDisplay ? 'col-6' : 'col-12'">
                            <table class="table table-bordered labs-print-table m-0">
                                <tbody>
                                    <tr class="labs-print-table-info" v-if="group.implantCrownOption || isTableDisplay">
                                        <td>
                                            <span class="text-muted">{{$t('Implant crowns')}}:</span>
                                        </td>
                                        <td>
                                            {{$t(capitalize(group.implantCrownOption))}}
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-info" v-if="group.systemName || isTableDisplay">
                                        <td>
                                            <span class="text-muted">{{$t('System name')}}:</span>
                                        </td>
                                        <td>
                                            {{$t(group.systemName)}}
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-info" v-if="group.diameter || isTableDisplay">
                                        <td>
                                            <span class="text-muted">{{$t('Diameter')}}:</span>
                                        </td>
                                        <td>
                                            {{$t(group.diameter)}}
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-info" v-if="group.abutmentType || isTableDisplay">
                                        <td>
                                            <span class="text-muted">{{$t('Abutment type')}}:</span>
                                        </td>
                                        <td>
                                            {{$t(group.abutmentType)}}
                                        </td>
                                    </tr>

                                    <template v-if="isTableDisplay">
                                        <tr class="labs-print-table-info">
                                            <td>
                                                <span class="text-muted">{{$t('When there is not enough space')}}:</span>
                                            </td>
                                            <td>
                                                {{$t(group.notEnoughSpace)}}
                                            </td>
                                        </tr>

                                        <tr class="labs-print-table-info">
                                            <td>
                                                <span class="text-muted">{{$t('Paint restoration')}}:</span>
                                            </td>
                                            <td>
                                                {{$t(capitalize(group.paintOption))}}
                                            </td>
                                        </tr>

                                        <tr class="labs-print-table-info">
                                            <td>
                                                <span class="text-muted">{{$t('Temporary restoration')}}:</span>
                                            </td>
                                            <td>
                                                {{$t(capitalize(group.temporaryRestoration))}}
                                            </td>
                                        </tr>

                                        <tr class="labs-print-table-info">
                                            <td>
                                                <span class="text-muted">{{$t('Form an ovoid impression on the model')}}:</span>
                                            </td>
                                            <td>
                                                {{group.ovoidImpression}}
                                            </td>
                                        </tr>

                                        <tr class="labs-print-table-info">
                                            <td>
                                                <span class="text-muted">{{$t('In the missing tooth area')}}:</span>
                                            </td>
                                            <td>
                                                {{group.missingTooth}}
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div> <!-- col-* -->
                        <div v-if="isTableDisplay" class="col-6">
                            <table class="table table-bordered labs-print-table m-0">
                                <tbody>
                                    <tr class="labs-print-table-info">
                                        <td>
                                            <span class="text-muted">{{$t('The color of the stump')}}:</span>
                                        </td>
                                        <td>
                                            {{group.colorStump}}
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-info">
                                        <td>
                                            <span class="text-muted">{{$t('The main color of the tooth')}}:</span>
                                        </td>
                                        <td>
                                            {{group.colorMainTeeth}}
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-info">
                                        <td>
                                            <span class="text-muted">{{$t('The color of the gums')}}:</span>
                                        </td>
                                        <td>
                                            {{group.colorGums}}
                                        </td>
                                    </tr>

                                    <!-- <tr class="labs-print-table-heading" v-if="group.abutmentType">
                                        <td colspan="2">
                                            <span class="text-capitalize text-muted">{{$t('abutment type')}}</span>
                                        </td>
                                    </tr>
                                    <tr class="labs-print-table-content" v-if="group.abutmentType">
                                        <td colspan="2">
                                            <abutment-select width="50px" height="50px" :value="group.abutmentType" unselectable></abutment-select>
                                        </td>
                                    </tr> -->

                                    <tr class="labs-print-table-heading">
                                        <td colspan="2">
                                            <span class="text-muted">{{$t("Frame")}}</span>
                                        </td>
                                    </tr>
                                    <tr class="labs-print-table-content">
                                        <td colspan="2">
                                            <frame-select unselectable width="35px" height="30px" :value="group.frame"></frame-select>
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-heading">
                                        <td colspan="2">
                                            <span class="text-muted">{{$t("Shoulder")}}</span>
                                        </td>
                                    </tr>
                                    <tr class="labs-print-table-content">
                                        <td colspan="2">
                                            <shoulder-select :label="true" :tooltip="false" unselectable width="30px" height="30px" :value="group.shoulder"></shoulder-select>
                                        </td>
                                    </tr>

                                    <tr class="labs-print-table-heading">
                                        <td colspan="2">
                                            <span class="text-muted">{{$t('Design of intermediate part')}}</span>
                                        </td>
                                    </tr>
                                    <tr class="labs-print-table-content">
                                        <td colspan="2">
                                            <intermediate-select unselectable width="30px" height="30px" :value="group.intermediate"></intermediate-select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div> <!-- col-* -->
                    </div>
                </template>
            </div>

            <div v-if="data.teeth[0].teeth.length > 14 && isTableDisplay" class="container page-break-after">
                <div class="row" v-for="(group, index) in data.teeth" :key="index">
                    <div class="col-6">
                        <table class="table table-bordered labs-print-table m-0">
                            <tbody>
                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Implant crowns')}}:</span>
                                    </td>
                                    <td>
                                        <template v-if="group.implantCrownOption">
                                            {{$t(capitalize(group.implantCrownOption))}}
                                        </template>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('System name')}}:</span>
                                    </td>
                                    <td>
                                        <template v-if="group.systemName">
                                            {{$t(group.systemName)}}
                                        </template>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Diameter')}}:</span>
                                    </td>
                                    <td>
                                        <template v-if="group.diameter">
                                            {{$t(group.diameter)}}
                                        </template>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Abutment type')}}:</span>
                                    </td>
                                    <td>
                                        <template v-if="group.abutmentType">
                                            {{$t(group.abutmentType)}}
                                        </template>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('When there is not enough space')}}:</span>
                                    </td>
                                    <td>
                                        {{$t(group.notEnoughSpace)}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Paint restoration')}}:</span>
                                    </td>
                                    <td>
                                        {{$t(capitalize(group.paintOption))}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Temporary restoration')}}:</span>
                                    </td>
                                    <td>
                                        {{$t(capitalize(group.temporaryRestoration))}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('Form an ovoid impression on the model')}}:</span>
                                    </td>
                                    <td>
                                        {{group.ovoidImpression}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('In the missing tooth area')}}:</span>
                                    </td>
                                    <td>
                                        {{group.missingTooth}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div> <!-- col-* -->
                    <div class="col-6">
                        <table class="table table-bordered labs-print-table m-0">
                            <tbody>
                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('The color of the stump')}}:</span>
                                    </td>
                                    <td>
                                        {{group.colorStump}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('The main color of the tooth')}}:</span>
                                    </td>
                                    <td>
                                        {{group.colorMainTeeth}}
                                    </td>
                                </tr>

                                <tr class="labs-print-table-info">
                                    <td>
                                        <span class="text-muted">{{$t('The color of the gums')}}:</span>
                                    </td>
                                    <td>
                                        {{group.colorGums}}
                                    </td>
                                </tr>

                                <!-- <tr class="labs-print-table-heading" v-if="group.abutmentType">
                                    <td colspan="2">
                                        <span class="text-capitalize text-muted">{{$t('abutment type')}}</span>
                                    </td>
                                </tr>
                                <tr class="labs-print-table-content" v-if="group.abutmentType">
                                    <td colspan="2">
                                        <abutment-select width="50px" height="50px" :value="group.abutmentType" unselectable></abutment-select>
                                    </td>
                                </tr> -->

                                <tr class="labs-print-table-heading">
                                    <td colspan="2">
                                        <span class="text-muted">{{$t("Frame")}}</span>
                                    </td>
                                </tr>
                                <tr class="labs-print-table-content">
                                    <td colspan="2">
                                        <frame-select unselectable width="30px" height="30px" :value="group.frame"></frame-select>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-heading">
                                    <td colspan="2">
                                        <span class="text-muted">{{$t("Shoulder")}}</span>
                                    </td>
                                </tr>
                                <tr class="labs-print-table-content">
                                    <td colspan="2">
                                        <shoulder-select :label="true" :tooltip="false" unselectable width="30px" height="30px" :value="group.shoulder"></shoulder-select>
                                    </td>
                                </tr>

                                <tr class="labs-print-table-heading">
                                    <td colspan="2">
                                        <span class="text-muted">{{$t('Design of intermediate part')}}</span>
                                    </td>
                                </tr>
                                <tr class="labs-print-table-content">
                                    <td colspan="2">
                                        <intermediate-select unselectable width="30px" height="30px" :value="group.intermediate"></intermediate-select>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div> <!-- col-* -->
                </div>
            </div>

            <div class="container">
                <div class="row print-page--images" v-if="data.images">
                    <div class="col-12 d-print-none" v-if="data.images.length > 0">
                        <hr class="mt-5 mb-5">
                    </div>
                    <div class="col-12 print-page--images__separator d-print-none" v-if="data.images.length > 0">
                        <custom-switch v-model="printImages" id="show-separated" class="mb-3" :title="$t('Print an image?')"></custom-switch>
                    </div>
                    <div class="col-12 page-break-after" v-if="printImages && data.images.length > 0">
                        <div class="row">
                            <div class="col-4" v-for="(image, index) in data.images" :key="index">
                                <div class="d-flex justify-content-center mb-4">
                                    <img :src="image.image.url" alt="..." style="max-width: 100%; width: 100%; object-fit: contain;">
                                </div>
                            </div>
                        </div>
                    </div>
                </div><!-- /.row print-page--images -->
            </div>
        </div>

        <template slot="modal-footer">
            <slot name="modal-footer">
                <div class="card-footer" style="display: block;">
                    <div class="row">
                        <div class="col"></div>
                        <div class="col-auto">
                            <button
                                class="btn mr-3 btn-outline-primary"
                                v-b-tooltip
                                :title="$t('Print')"
                                @click="print()"
                            >
                                <i class="fe fe-printer"></i>
                            </button>
                            <button class="btn btn-outline-secondary ml-2" @click="hide">
                                {{$t("Close")}}
                            </button>
                        </div>
                    </div>
                </div>
            </slot>
        </template>
    </modal>
</template>

<script>
    import {mapGetters, mapState, mapActions} from "vuex";
    import Modal from "@/components/Modal.vue";
    import {modalMixins} from '@/mixins/modal.js'
    import {teethComponents} from '@/mixins/all-teeth.js'
    import FrameSelect from "@/components/Lab/Frame/FrameSelect";
    import CustomSwitch from "@/components/CustomSwitch";
    import ShoulderSelect from "@/components/Lab/Shoulder/ShoulderSelect";
    import IntermediateSelect from "@/components/Lab/Intermediate/IntermediateSelect";
    import CompanyPrintHeader from "@/components/Company/CompanyPrintHeader.vue";
    import Paint from "@/components/Lab/Paint/Paint";
    import TeethMap from '@/components/Teeth/Map';
    // import AbutmentSelect from "@/components/Lab/Abutment/AbutmentSelect";

    export default {
        name: "PrintLabModal",
        props: ['id'],
        mixins: [modalMixins, teethComponents],
        components: {
            // AbutmentSelect,
            Paint,
            TeethMap,
            FrameSelect,
            CustomSwitch,
            ShoulderSelect,
            IntermediateSelect,
            CompanyPrintHeader,
            Modal,
        },
        data() {
            return {
                lab: {teeth: []},
                title: '',
                width: '50px',
                height: '50px',
                printImages: false,
                isTableDisplay: false
            }
        },
        methods: {
            ...mapActions('company', {getTechnicians: 'get'}),
            ...mapActions('customers', {getCustomers: 'get'}),

            shown() {
                const {Lab} = this.$FeathersVuex;
                let data = Lab.getFromStore('temp');
                if (data) {
                    delete data._id;
                    this.lab = this.$copy(data['model']);
                    new Lab({
                        model: {},
                        _id: 'temp'
                    });
                }

                this.getTechnicians(this.data.technicianId)
                if ( !this.customer )
                    this.getCustomers(this.data.customerId)

                this.printImages = false;
                this.isTableDisplay = false;
                this.tableDisplay(this.lab);
            },

            hidden() {
                var $printSection = document.getElementById("printSection");
                if ($printSection) {
                    $printSection.remove();
                }
            },

            hide() {
                this.$root.$emit('bv::hide::modal', this.id)
            },

            print() {
                var domClone = document.getElementById("printContent").cloneNode(true);

                var $printSection = document.getElementById("printSection");
                if (!$printSection) {
                    var $printSection = document.createElement("div");
                    $printSection.id = "printSection";
                    document.body.appendChild($printSection);
                }

                $printSection.innerHTML = "";
                $printSection.appendChild(domClone);
                window.print();
            },

            tableDisplay(data) {
                let arr = [
                    'frame',
                    'shoulder',
                    'colorStump',
                    'colorMainTeeth',
                    'colorGums',
                    'notEnoughSpace',
                    'intermediate',
                    'paintOption',
                    'temporaryRestoration',
                    'missingTooth',
                    'ovoidImpression'];

                if (data.teeth && data.teeth.length > 0)
                    data.teeth.forEach(group => {
                        Object.keys(group).forEach(option => {
                            if (arr.includes(option))
                                this.isTableDisplay = true;
                                return;
                        });
                    });
            },

            capitalize(string) {
                return string ? string[0].toUpperCase() + string.slice(1) : null
            },

            dateFormatHandler(string) {
                return this.$moment(string).format('DD MMMM, YYYY');
            }
        },
        computed: {
            ...mapState('auth', ['user']),
            ...mapGetters('customers', {getCustomer: 'get'}),
            ...mapGetters('company', {getTechnicianFromStore: 'get'}),

            technician() {
                if (this.lab.technicianId) return this.getTechnicianFromStore(this.lab.technicianId);
            },
            customer() {
                return this.getCustomer(this.lab.customerId);
            },

            data() {
                return this.lab || {}
            },
            teethNumbers() {
                return this.lab.teeth.map(group=>(group.teeth.map(toothNumber=>(toothNumber))))[0];
            }
        },
    }
</script>

<style lang="scss" scoped>

    .empty-area {
        display: inline-block;
        height: 20px;
        border-bottom: 1px solid #95AAC9;
    }

    .labs-print-table {
        &-heading {
            text-align: center;
            background: #f9fbfd;
            color: #95aac9;
            font-weight: 900;
            td {
                padding: 10px 0;
                font-size: 14px;
            }
        }
        &-info {
            td {
                padding: 5px;
                text-align: left;
                &:first-child {
                    width: 35%;
                }
                &:last-child {
                    width: 65%;
                    text-align: left;
                }
            }
        }
        &-content {
            td {
                padding: 0;
            }
        }
    }

    @media screen {
        #printContent {
            position: relative;
            height: 78vh;
            width: 100%;
            overflow: auto;
            overflow-x: hidden;
        }
    }

    .labs-modal-logo {
        max-width: 7rem;
    }

    @media only screen and (max-width: 576px) {
        .labs-modal-logo {
            max-width: 5rem;
            margin-bottom: 0!important;
        }
    }

</style>
